import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Navigation } from "lucide-react";
import { WhatsAppIcon, TikTokIcon } from "@/components/SocialIcons";
import { LOGO_URL, CONTACT, SOCIALS } from "@/config";
import { useI18n } from "@/i18n";

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  tiktok: TikTokIcon,
  whatsapp: WhatsAppIcon,
};

export const Footer = () => {
  const { t } = useI18n();
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className="bg-navy-deep text-white pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Sedlo Services logo" className="h-14 w-auto bg-white rounded-md p-2" />
            <p className="mt-4 text-white/60 text-sm max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="flex gap-3 mt-6">
              {SOCIALS.map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${s.icon}`}
                    aria-label={s.name}
                    className="w-10 h-10 rounded-md bg-white/5 hover:bg-brand flex items-center justify-center transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="font-head font-semibold text-sm uppercase tracking-wider text-brand mb-4">{t.footer.navTitle}</h4>
            <ul className="space-y-2 text-sm text-white/70">
              {t.footer.links.map(([l, h]) => (
                <li key={h}>
                  <button onClick={() => go(h)} className="hover:text-brand transition-colors">{l}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-head font-semibold text-sm uppercase tracking-wider text-brand mb-4">{t.footer.contactTitle}</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href={CONTACT.phoneHref} className="hover:text-brand">{CONTACT.phone}</a></li>
              <li><a href={`mailto:${CONTACT.email}`} className="hover:text-brand">{CONTACT.email}</a></li>
              <li>{CONTACT.addressLine1}</li>
              <li>{CONTACT.addressLine2}</li>
            </ul>
            <a
              data-testid="footer-directions-btn"
              href={CONTACT.directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lift inline-flex items-center gap-2 mt-4 bg-brand hover:bg-brand-hover text-white text-sm font-semibold px-4 py-2.5 rounded-md"
            >
              <Navigation className="w-4 h-4" /> {t.footer.directions}
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-white/50">
          <div className="flex flex-col sm:flex-row items-center gap-x-3 gap-y-1 text-center sm:text-left">
            <p>© {new Date().getFullYear()} Sedlo Services. {t.footer.rights}</p>
            <p data-testid="footer-author" className="text-white/40">
              {t.footer.authorPre}
              <Link to="/" className="hover:text-brand transition-colors">Sedlo Services</Link>
            </p>
          </div>
          <Link to="/ochrana-osobnych-udajov" data-testid="footer-privacy-link" className="hover:text-brand transition-colors">
            {t.footer.privacy}
          </Link>
        </div>
      </div>
    </footer>
  );
};
